// tanstack
import { useParams } from '@tanstack/react-router';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';

/**
 * Helper to parse account IDs from claims array string
 * Claims come as strings like "[ 9297, 8810 ]"
 */
const parseAccountIds = (claimValue) => {
  if (!claimValue) return [];
  try {
    // Remove spaces and parse as JSON array
    const parsed = JSON.parse(claimValue.replace(/\s/g, ''));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Custom hook to check account-level permissions for the current user on the current account.
 *
 * Note: This is different from useIsAdmin() which checks for superuser status.
 * - Superuser: Internal company admin with access to all features, bypasses all limitations
 * - Account roles: Determined by JWT claims (accounts.manage, accounts.write, accounts.read)
 *
 * @returns {Object} Permission flags and role information
 * @property {boolean} isReadOnly - User has ReadOnly role - cannot make any changes
 * @property {boolean} isUser - User has User role - can edit but cannot manage users
 * @property {boolean} isAdmin - User has Administrator role on this account (not superuser)
 * @property {boolean} canEdit - User can make changes (!isReadOnly)
 * @property {boolean} canManageUsers - User can add/remove users (Administrator or superuser)
 * @property {string|null} accountRole - "ReadOnly" | "User" | "Administrator" | null
 * @property {boolean} isSuperUser - Platform-wide admin (bypasses all account-level restrictions)
 *
 * @example
 * const { isReadOnly, canManageUsers } = useAccountPermissions();
 *
 * // Disable actions for readonly users
 * <Button disabled={isReadOnly} onClick={handleSave}>Save</Button>
 *
 * // Hide admin-only sections
 * {canManageUsers && <UserManagementPanel />}
 */
export const useAccountPermissions = () => {
  const { accountId } = useParams({ strict: false });
  const { session } = useSessionAuth();

  const claims = session?.claims || {};

  // SuperUser is company admin - bypasses all account-level restrictions
  const isSuperUser = claims['accounts.superuser'] === 'True';

  // Parse account access from JWT claims
  const manageAccounts = parseAccountIds(claims['accounts.manage']);
  const writeAccounts = parseAccountIds(claims['accounts.write']);
  const readAccounts = parseAccountIds(claims['accounts.read']);

  // Convert accountId to number for comparison
  const accountIdNum = accountId ? Number(accountId) : null;

  // Determine account role based on which claim array contains the account
  // Priority: manage > write > read
  let accountRole = null;
  if (accountIdNum) {
    if (manageAccounts.includes(accountIdNum)) {
      accountRole = 'Administrator';
    } else if (writeAccounts.includes(accountIdNum)) {
      accountRole = 'User';
    } else if (readAccounts.includes(accountIdNum)) {
      accountRole = 'ReadOnly';
    }
  }

  // Derive permission flags from account role
  // SuperUser bypasses readonly restrictions but we still track the account role
  const isReadOnly = accountRole === 'ReadOnly' && !isSuperUser;
  const isUser = accountRole === 'User';
  const isAdmin = accountRole === 'Administrator';

  // Permission helpers
  const canEdit = !isReadOnly; // Can make changes if not readonly
  const canManageUsers = isAdmin || isSuperUser; // Only admins and superusers can manage users

  return {
    isReadOnly,
    isUser,
    isAdmin,
    canEdit,
    canManageUsers,
    accountRole,
    isSuperUser,
  };
};
